import React, { useRef } from "react";
import { Form, Button, Label } from "semantic-ui-react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixIso14091form } from "../../../../api";
import { initialValues, validationSchema } from "./Iso14091Form.form";
import "../EnvironmentalRiskMatrixForm/EnvironmentalRiskMatrixForm.scss";

const environmentalRiskMatrixIso14091Ctrl = new EnvironmentalRiskMatrixIso14091form();

// Hazards climáticos (Taxonomía UE / IPCC) — sugerencias de referencia,
// editables por la organización.
const DRIVER_SUGGESTIONS = [
  "Ola de calor",
  "Sequía / estrés hídrico",
  "Inundación (fluvial)",
  "Inundación (pluvial / lluvias intensas)",
  "Inundación costera",
  "Ciclón / huracán / tifón",
  "Tormenta (incl. granizo, viento)",
  "Incendio forestal",
  "Ola de frío / helada",
  "Aumento del nivel del mar",
  "Aumento de temperatura media",
  "Cambio de patrones de precipitación",
  "Deslizamiento de tierra",
  "Erosión / degradación de suelo",
];

function toOptions(list) {
  return list.map((v) => ({ key: v, value: v, text: v }));
}

// Misma fórmula que server/controllers/environmentalRiskMatrixIso14091.js.
function computePreview(values) {
  const exposure = Number(values.exposure) || 0;
  const sensitivity = Number(values.sensitivity) || 0;
  const adaptiveCapacity = Number(values.adaptiveCapacity) || 0;

  let vulnerability = 0;
  if (sensitivity && adaptiveCapacity) {
    vulnerability = Math.max(1, Math.min(5, Math.round((sensitivity + (6 - adaptiveCapacity)) / 2)));
  }
  return { vulnerability, level: vulnerability * exposure };
}

export function Iso14091Form(props) {
  const { onClose, onReload, item, siteSelected } = props;

  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const submittingRef = useRef(false);
  const scaleOptions = [1, 2, 3, 4, 5].map((n) => ({ key: n, value: n, text: String(n) }));

  const formik = useFormik({
    initialValues: initialValues(item),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        if (!item) {
          const res = await environmentalRiskMatrixIso14091Ctrl.createEnvironmentalRiskMatrixIso14091(accessToken, {
            ...formValue,
            site: user?.site || siteSelected,
          });
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        } else {
          const res = await environmentalRiskMatrixIso14091Ctrl.updateEnvironmentalRiskMatrixIso14091(accessToken, item._id, formValue);
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        }
        setTimeout(() => {
          onReload?.();
          onClose?.();
        }, 800);
      } catch (error) {
        toast.error(error?.msg || error?.message || t("server_connection_error"));
      } finally {
        submittingRef.current = false;
      }
    },
  });

  const preview = computePreview(formik.values);

  return (
    <Form className="risk-matrix-form" onSubmit={formik.handleSubmit}>
      <h3 className="risk-matrix-form__title">{item ? t("risk_matrix_edit_item") : t("risk_matrix_new_item")}</h3>
      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.title && !!formik.errors.title}>
          <label>{t("title")}</label>
          <Form.Input
            name="title"
            placeholder={t("risk_matrix_title_placeholder")}
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.title && formik.errors.title && (
            <Label basic color="red" pointing>{formik.errors.title}</Label>
          )}
        </Form.Field>

        <Form.Field>
          <label>{t("risk_matrix_iso14091_driver")}</label>
          <Form.Dropdown
            selection
            search
            allowAdditions
            clearable
            options={toOptions(DRIVER_SUGGESTIONS)}
            value={formik.values.driver}
            placeholder={t("risk_matrix_ifrs_driver_placeholder")}
            onAddItem={(_, { value }) => DRIVER_SUGGESTIONS.push(value)}
            onChange={(_, { value }) => formik.setFieldValue("driver", value)}
          />
        </Form.Field>
      </div>

      <Form.Field>
        <label>{t("description")}</label>
        <Form.TextArea name="description" value={formik.values.description} onChange={formik.handleChange} />
      </Form.Field>

      <Form.Field>
        <label>{t("risk_matrix_iso14091_chain")}</label>
        <Form.Input
          name="chain"
          placeholder={t("risk_matrix_iso14091_chain_placeholder")}
          value={formik.values.chain}
          onChange={formik.handleChange}
        />
      </Form.Field>

      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.exposure && !!formik.errors.exposure}>
          <label>{t("risk_matrix_iso14091_exposure")}</label>
          <Form.Select
            options={scaleOptions}
            placeholder="1-5"
            value={formik.values.exposure}
            onChange={(_, { value }) => formik.setFieldValue("exposure", value)}
            onBlur={() => formik.setFieldTouched("exposure", true)}
          />
          {formik.touched.exposure && formik.errors.exposure && (
            <Label basic color="red" pointing>{formik.errors.exposure}</Label>
          )}
        </Form.Field>

        <Form.Field error={formik.touched.sensitivity && !!formik.errors.sensitivity}>
          <label>{t("risk_matrix_iso14091_sensitivity")}</label>
          <Form.Select
            options={scaleOptions}
            placeholder="1-5"
            value={formik.values.sensitivity}
            onChange={(_, { value }) => formik.setFieldValue("sensitivity", value)}
            onBlur={() => formik.setFieldTouched("sensitivity", true)}
          />
          {formik.touched.sensitivity && formik.errors.sensitivity && (
            <Label basic color="red" pointing>{formik.errors.sensitivity}</Label>
          )}
        </Form.Field>
      </div>

      <Form.Field error={formik.touched.adaptiveCapacity && !!formik.errors.adaptiveCapacity}>
        <label>{t("risk_matrix_iso14091_adaptive_capacity")}</label>
        <Form.Select
          options={scaleOptions}
          placeholder="1-5"
          value={formik.values.adaptiveCapacity}
          onChange={(_, { value }) => formik.setFieldValue("adaptiveCapacity", value)}
          onBlur={() => formik.setFieldTouched("adaptiveCapacity", true)}
        />
        {formik.touched.adaptiveCapacity && formik.errors.adaptiveCapacity && (
          <Label basic color="red" pointing>{formik.errors.adaptiveCapacity}</Label>
        )}
      </Form.Field>

      {(formik.values.sensitivity || formik.values.adaptiveCapacity) && (
        <div className="risk-matrix-form__preview">
          <span>{t("risk_matrix_iso14091_vulnerability")}: <b>{preview.vulnerability}</b></span>
          <span>{t("risk_matrix_level")}: <b>{preview.level}</b></span>
        </div>
      )}

      <Form.Field>
        <label>{t("risk_matrix_controls")}</label>
        <Form.TextArea name="controls" value={formik.values.controls} onChange={formik.handleChange} />
      </Form.Field>

      <Form.Field>
        <label>{t("risk_matrix_action")}</label>
        <Form.TextArea name="action" value={formik.values.action} onChange={formik.handleChange} />
      </Form.Field>

      <div className="risk-matrix-form__actions">
        <Button type="submit" primary loading={formik.isSubmitting} disabled={formik.isSubmitting}>
          {!item ? t("save") : t("update")}
        </Button>
        <Button type="button" onClick={() => onClose?.()}>
          {t("cancel")}
        </Button>
      </div>
    </Form>
  );
}
